/**
 * Sessão da suíte em memória (SPEC-v2 2.1, 2.2): quem está usando, com as permissões já calculadas
 * pelo provider de login. Fonte única para o `AuthGate`, o lançador, os shells das áreas e os
 * providers de fixture (checagem de permissão). Sem zustand: um store externo mínimo, para caber no
 * caminho de `/entrar`.
 */
import type { Area, SessionInfo, StudioPermission, SuitePermission } from '@rota/contracts';
import { useSyncExternalStore } from 'react';

export type SessionStatus = 'unknown' | 'loading' | 'signed_in' | 'signed_out';

interface SessionState {
  status: SessionStatus;
  session: SessionInfo | null;
  /** Neste navegador ainda não há dono configurado (leva a `/configurar`). */
  needsSetup: boolean;
}

let state: SessionState = { status: 'unknown', session: null, needsSetup: false };
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export const sessionStore = {
  get: (): SessionState => state,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  set(patch: Partial<SessionState>): void {
    state = { ...state, ...patch };
    emit();
  },
  signedIn(session: SessionInfo): void {
    state = { status: 'signed_in', session, needsSetup: false };
    emit();
  },
  signedOut(needsSetup = false): void {
    state = { status: 'signed_out', session: null, needsSetup };
    emit();
  },
};

/** Sessão atual fora do React (providers de fixture, guarda de rota). */
export function currentSession(): SessionInfo | null {
  return state.session;
}

export function useSessionState(): SessionState {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.get, sessionStore.get);
}

export function useSession(): SessionInfo | null {
  return useSessionState().session;
}

// ---------------------------------------------------------------------------
// Leitura de permissão (a UI só esconde; quem barra é o provider)
// ---------------------------------------------------------------------------

export function hasSuite(s: SessionInfo | null | undefined, p: SuitePermission): boolean {
  return !!s && s.suitePermissions.includes(p);
}

export function hasStudio(s: SessionInfo | null | undefined, p: StudioPermission): boolean {
  return !!s && s.studioPermissions.includes(p);
}

export function canOpenArea(s: SessionInfo | null | undefined, area: Area): boolean {
  return !!s && s.areas.includes(area);
}

/** O step-up (senha ou código nos últimos 15 min) ainda vale? */
export function stepUpFresh(s: SessionInfo | null | undefined, now = Date.now()): boolean {
  return !!s?.stepUpUntil && s.stepUpUntil > now;
}
